import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from '@pwc/appkit-react';

import { defaultSideBarWithColumnsToolPanel } from '../../shared/displayComponents/agGrid/constants';
import AgGrid from '../../shared/displayComponents/agGrid/agGrid.component';
import { useRowEditMode } from '../../shared/editMode';
import useModal from '../../shared/hooks/useModal.hook';
import { columnBlueprintHeaderOptions } from '../../shared/columnDefinitions/constants';
import columnBlueprintStyles from '../../shared/styles/columnBlueprintStyles.module.scss';
import useUrlParams from '../../sharedSubPages/returnWorkspace/hooks/useUrlParams.hook';
import {
  taxYearSelector,
  periodSelector,
  consolidationsOptionsSelector,
  jurisdictionIdSelector,
  isFetchingJurisdictionsSelector,
  jurisdictionStateCodeSelector,
} from '../store/selectors';
import { EverywhereJurisdictionDropdownOption } from '../../shared/constants';

import validateCalcSpecs from './validateCalcSpecs';
import ValidationErrorModal from './validationErrorModal.component';
import getColumnBlueprintBasedColumnDefinitions from './calcSpecs.columnDefinition';
import {
  fetchConspbCalcSpecs,
  updateConspbCalcSpecs,
  createConspbCalcSpecs,
  blockContextChanges,
} from './store/actions';
import {
  conspbCalcSpecsSelector,
  conspbCalcSpecsColumnsBlueprintSelector,
  isFetchingConspbCalcSpecsSelector,
  isFetchingCalcSpecsOptionsSelector,
  accountOptionsSelector,
  categoryOptionsSelector,
  groupedFilingAttributesSelector,
  isEditingConspbCalcSpecsSelector,
} from './store/selectors';
import { calcSpecTypes } from './constants';

const getFormattedValues = row => ({
  ...row,
  multiplier: parseFloat(row.multiplier),
  period: Number(row.period),
  displayOrder: Number(row.displayOrder),
  calcPreferences: Number(row.calcPreferences),
  accountJurisdictionId: String(row.accountJurisdictionId),
});

const getUniqueRowId = ({ data: { rowId } }) => rowId;

const fillCells = params => params.initialValues[0];

const ConspbCalcSpec = ({ categories = [], hasUserPermissionsToEdit }) => {
  const { queryParams, setParams } = useUrlParams();
  const [validationResult, setValidationResult] = useState({});
  const [addedRowsCounter, setAddedRowsCounter] = useState(0);

  const dispatch = useDispatch();

  const taxYear = useSelector(taxYearSelector);
  const period = useSelector(periodSelector);
  const jurisdictionId = useSelector(jurisdictionIdSelector);
  const isFetchingJurisdictions = useSelector(isFetchingJurisdictionsSelector);
  const jurisdictionStateCode = useSelector(jurisdictionStateCodeSelector);

  const calcSpecs = useSelector(conspbCalcSpecsSelector);
  const calcSpecsColumnsBlueprint = useSelector(conspbCalcSpecsColumnsBlueprintSelector);
  const isFetchingCalcSpecs = useSelector(isFetchingConspbCalcSpecsSelector);

  const accountOptions = useSelector(accountOptionsSelector);
  const categoryOptions = useSelector(categoryOptionsSelector);
  const consolidationOptions = useSelector(consolidationsOptionsSelector);
  const groupedFilingAttributes = useSelector(groupedFilingAttributesSelector);
  const isFetchingCalcSpecsOptions = useSelector(isFetchingCalcSpecsOptionsSelector);

  const isEditingConspbCalcSpecs = useSelector(isEditingConspbCalcSpecsSelector);

  useEffect(() => {
    if (queryParams.search || queryParams.search === '') {
      setParams({ queryParams: { search: null } });
    }
  }, [setParams, queryParams.search]);

  const { showModal: showValidationErrorModal, modalProps } = useModal();

  const isContextReady = taxYear && period && jurisdictionId;

  const calcSpecInitialValues = useMemo(
    () => ({
      consolId: jurisdictionStateCode,
      categoryId: '',
      subcategory: '',
      accountId: '',
      accountJurisdictionId: '',
      multiplier: '1',
      displayOrder: '100',
      calcPreferences: '0',
    }),
    [jurisdictionStateCode],
  );

  const accountJurisdictionOptions = useMemo(
    () => [{ value: jurisdictionId, label: jurisdictionId }, EverywhereJurisdictionDropdownOption],
    [jurisdictionId],
  );

  const displayNameAndSelectOptionDictionary = useMemo(
    () => ({
      accountId: accountOptions,
      categoryId: categoryOptions,
      consolId: [
        { label: jurisdictionStateCode, value: jurisdictionStateCode },
        ...consolidationOptions,
      ],
      accountJurisdictionId: accountJurisdictionOptions,
    }),
    [
      accountOptions,
      categoryOptions,
      consolidationOptions,
      accountJurisdictionOptions,
      jurisdictionStateCode,
    ],
  );

  useEffect(() => {
    if (isContextReady) {
      dispatch(fetchConspbCalcSpecs({ taxYear, period, jurisdictionId }));
    }
  }, [dispatch, isContextReady, taxYear, period, jurisdictionId]);

  const isLoading =
    !isContextReady ||
    isFetchingCalcSpecs ||
    isFetchingCalcSpecsOptions ||
    isEditingConspbCalcSpecs ||
    isFetchingJurisdictions;

  const onSaveCalcSpec = useCallback(
    async ({ rowsPairsWithChanges, rowsToAdd }) => {
      const formattedModifiedRows = rowsPairsWithChanges.map(({ newRow }) => {
        const formattedValues = getFormattedValues(newRow);

        return { ...newRow, ...formattedValues };
      });

      const formattedAddedRows = rowsToAdd.map(newRow => {
        const formattedValues = getFormattedValues(newRow);

        return {
          ...newRow,
          ...formattedValues,
          taxYear: Number(taxYear),
          period: Number(period),
          jurisdictionId,
        };
      });

      const { duplicatedRowsIds, rowsWithInvalidCells, modalMessage } = validateCalcSpecs({
        addOrModifiedRows: [...formattedModifiedRows, ...formattedAddedRows],
        calcSpecs,
        displayNameAndSelectOptionDictionary,
        calcSpecType: calcSpecTypes.CONSPB,
        groupedFilingAttributes,
      });

      if (modalMessage) {
        showValidationErrorModal(modalMessage);
        setValidationResult({ duplicatedRowsIds, rowsWithInvalidCells });
        return { shouldCancelHookStateReset: true };
      }

      if (rowsPairsWithChanges.length) {
        await dispatch(
          updateConspbCalcSpecs({
            values: formattedModifiedRows,
            taxYear,
            period,
            jurisdictionId,
          }),
        );
      }

      if (rowsToAdd.length) {
        await dispatch(
          createConspbCalcSpecs({
            values: formattedAddedRows,
            taxYear,
            period,
            jurisdictionId,
          }),
        );
      }

      setAddedRowsCounter(0);
      setValidationResult({});
      dispatch(blockContextChanges(false));
      dispatch(fetchConspbCalcSpecs({ taxYear, period, jurisdictionId }));
    },
    [
      showValidationErrorModal,
      setValidationResult,
      setAddedRowsCounter,
      dispatch,
      jurisdictionId,
      taxYear,
      period,
      calcSpecs,
      displayNameAndSelectOptionDictionary,
      groupedFilingAttributes,
    ],
  );

  const onEnterEdit = useCallback(() => {
    dispatch(blockContextChanges(true));
  }, [dispatch]);

  const onCancelEdit = useCallback(() => {
    setValidationResult({});
    dispatch(blockContextChanges(false));
  }, [setValidationResult, dispatch]);

  const filteredRowData = useMemo(() => {
    const selectedCategoriesSet = new Set(categories);
    return calcSpecs.filter(({ categoryId }) => selectedCategoriesSet.has(categoryId));
  }, [calcSpecs, categories]);

  const {
    navigationPrompt,
    isInEditMode,
    setIsInEditMode,
    editModeButtons,
    clonedRowData,
    updateRow,
    addRow,
    deleteRow,
    onGridReady,
  } = useRowEditMode({
    onEnter: onEnterEdit,
    onSave: onSaveCalcSpec,
    onCancel: onCancelEdit,
    rowData: filteredRowData,
    getUniqueRowId,
    editButtonDisabled: isLoading || !filteredRowData,
    appendRowUnderSelectedRow: true,
  });

  const addCalcSpec = useCallback(() => {
    addRow({ ...calcSpecInitialValues, isAddedRow: true, rowId: String(addedRowsCounter) });
    setAddedRowsCounter(addedRowsCounter + 1);
    setIsInEditMode(true);
    dispatch(blockContextChanges(true));
  }, [
    addRow,
    setAddedRowsCounter,
    setIsInEditMode,
    dispatch,
    addedRowsCounter,
    calcSpecInitialValues,
  ]);

  const columnDefinitions = useMemo(
    () =>
      getColumnBlueprintBasedColumnDefinitions({
        updateRow,
        onDeleteIconClick: deleteRow,
        isInEditMode,
        columnsBlueprint: calcSpecsColumnsBlueprint,
        displayNameAndSelectOptionDictionary,
        validationResult,
      }),
    [
      updateRow,
      deleteRow,
      isInEditMode,
      calcSpecsColumnsBlueprint,
      displayNameAndSelectOptionDictionary,
      validationResult,
    ],
  );

  return (
    <>
      {navigationPrompt}
      <ValidationErrorModal {...modalProps} />
      <div className="add-button-column">
        {hasUserPermissionsToEdit && (
          <>
            <Button size="lg" className="add-button" onClick={addCalcSpec} disabled={isLoading}>
              Add Calc Spec
            </Button>
            {editModeButtons}
          </>
        )}
      </div>
      <div className={`row grid-row ${columnBlueprintStyles.gridContainer}`}>
        <div className="col">
          <AgGrid
            rowData={categories.length ? clonedRowData : []}
            columnDefs={columnDefinitions}
            isGridLoading={isLoading}
            onGridReady={onGridReady}
            initialQuickFilterValue={queryParams.search || ''}
            withSearchBar
            enableRangeSelection
            suppressCopyRowsToClipboard
            suppressMultiRangeSelection
            enableFillHandle={isInEditMode}
            fillOperation={fillCells}
            undoRedoCellEditing
            stopEditingWhenCellsLoseFocus
            sideBar={defaultSideBarWithColumnsToolPanel}
            {...columnBlueprintHeaderOptions}
            enableBrowserTooltips
            areHeaderCellBordersEnabled
          />
        </div>
      </div>
    </>
  );
};

ConspbCalcSpec.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string.isRequired),
  hasUserPermissionsToEdit: PropTypes.bool.isRequired,
};

export default ConspbCalcSpec;
